import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Instagram, Youtube, ShieldCheck, Edit3, Loader2, X, AlertCircle, Check, Info } from "lucide-react";
import { toast } from "sonner";
import VerifiedAnalyticsBadge from "../VerifiedAnalyticsBadge";
import { useAuth } from "../../context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PROVIDERS = [
  { id: "instagram", label: "Instagram", Icon: Instagram, hint: "Business or Creator account linked to a Facebook Page (required for OAuth audience data)." },
  { id: "youtube",   label: "YouTube",   Icon: Youtube,   hint: "Any YouTube channel — sign in with the Google account that owns it." },
];

/**
 * Social account connections for the athlete Settings page.
 *
 * Two paths per provider:
 *   1. OAuth — opens a popup to /api/oauth/{provider}/start. When live, this
 *      returns API-verified audience data. When OAuth isn't configured on
 *      this deployment, the button is disabled with an explanation.
 *   2. Manual entry — always available. For personal-account athletes who
 *      can't pass Meta's professional-account requirement. Marked as
 *      "self-reported" so the Media Kit shows the right trust badge.
 */
export default function SocialConnectionsCard() {
  const { user } = useAuth();
  const [conns, setConns] = useState([]);
  const [oauthEnabled, setOauthEnabled] = useState({ instagram: false, youtube: false });
  const [loaded, setLoaded] = useState(false);
  const [manualOpen, setManualOpen] = useState(null); // provider id or null

  const load = () => api.get("/social-connections")
    .then(({ data }) => {
      setConns(data.connections || []);
      setOauthEnabled(data.oauth_enabled || {});
    })
    .catch(() => {})
    .finally(() => setLoaded(true));

  useEffect(() => {
    load();
    // Catch the popup -> opener postMessage when an OAuth flow completes.
    const handler = (event) => {
      if (event.origin !== window.location.origin) return;
      const msg = event.data;
      if (msg?.type === "oauth_instagram_complete" || msg?.type === "oauth_youtube_complete") {
        if (msg.success) toast.success("Account connected"); else toast.error(msg.error || "Connection failed");
        load();
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const connectOAuth = (provider) => {
    if (!oauthEnabled[provider]) {
      toast.info("OAuth isn't configured on this deployment yet. Use 'Add manually' below.");
      return;
    }
    const w = 600, h = 720;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top  = window.screenY + (window.outerHeight - h) / 2;
    window.open(`${BACKEND}/api/oauth/${provider}/start`, `${provider}_oauth`,
      `width=${w},height=${h},left=${left},top=${top}`);
  };

  const disconnect = async (provider) => {
    try {
      await api.delete(`/social-connections/${provider}`);
      toast.success("Disconnected.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to disconnect");
    }
  };

  if (!loaded) return null;

  return (
    <div className="surface p-6" data-testid="social-connections-card">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="label-eyebrow">Verified audience data</div>
          <h2 className="font-heading text-2xl uppercase tracking-tight mt-1">Social accounts</h2>
        </div>
        <VerifiedAnalyticsBadge connections={conns} plan={user?.plan} />
      </div>
      <p className="text-xs text-ink-muted mb-6 leading-relaxed max-w-2xl">
        Connect Instagram + YouTube so your media kit shows <span className="text-volt">API-verified</span> follower counts and audience demographics. Brands trust verified data 3× more than self-reported claims.
      </p>

      <div className="space-y-3">
        {PROVIDERS.map(p => {
          const conn = conns.find(c => c.provider === p.id);
          return (
            <ProviderRow key={p.id} provider={p} conn={conn}
              oauthEnabled={oauthEnabled[p.id]}
              onConnect={() => connectOAuth(p.id)}
              onDisconnect={() => disconnect(p.id)}
              onManual={() => setManualOpen(p.id)} />
          );
        })}
      </div>

      {/* Personal-account fallback note */}
      <div className="mt-6 border border-line p-4 flex items-start gap-3" data-testid="personal-account-note">
        <Info className="w-4 h-4 text-volt mt-0.5 flex-shrink-0" />
        <p className="text-xs text-ink-muted leading-relaxed">
          <span className="text-white">Personal account?</span> Instagram only returns audience demographics for <span className="text-white">Business or Creator</span> accounts linked to a Facebook Page. If yours is personal, just <button onClick={() => setManualOpen("instagram")} className="text-volt hover:text-white underline" data-testid="personal-add-manual-link">add your numbers manually</button> — they'll show on your media kit clearly labelled as self-reported. Switch to a free Creator account anytime to upgrade to verified.
        </p>
      </div>

      {manualOpen && (
        <ManualEntryModal provider={manualOpen}
          existing={conns.find(c => c.provider === manualOpen && c.source === "manual")}
          onClose={() => setManualOpen(null)}
          onSaved={() => { setManualOpen(null); load(); }} />
      )}
    </div>
  );
}

function ProviderRow({ provider, conn, oauthEnabled, onConnect, onDisconnect, onManual }) {
  const Icon = provider.Icon;
  const isOauth = conn?.source === "oauth";
  const isManual = conn?.source === "manual";
  const isPendingReview = conn?.status === "pending_review";
  return (
    <div className="border border-line p-4 flex flex-wrap items-center gap-4" data-testid={`social-row-${provider.id}`}>
      <div className="w-10 h-10 border border-line flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-volt" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-heading text-lg uppercase">{provider.label}</div>
          {isOauth && !isPendingReview && (
            <span className="px-2 py-0.5 text-[9px] uppercase tracking-widest border border-volt text-volt inline-flex items-center gap-1" data-testid={`badge-verified-${provider.id}`}>
              <ShieldCheck className="w-3 h-3" /> Verified
            </span>
          )}
          {isManual && (
            <span className="px-2 py-0.5 text-[9px] uppercase tracking-widest border border-line text-ink-muted" data-testid={`badge-manual-${provider.id}`}>
              Self-reported
            </span>
          )}
          {isPendingReview && (
            <span className="px-2 py-0.5 text-[9px] uppercase tracking-widest border border-amber-400/60 text-amber-400" data-testid={`badge-pending-${provider.id}`}>
              Sync pending
            </span>
          )}
        </div>
        {conn ? (
          <div className="text-xs text-ink-muted mt-1 break-words leading-relaxed">
            {conn.handle && <>@{conn.handle}</>}
            {conn.metrics?.followers != null && <> · {conn.metrics.followers.toLocaleString()} followers</>}
            {conn.last_synced_at && <> · updated {new Date(conn.last_synced_at).toLocaleDateString()}</>}
          </div>
        ) : (
          <div className="text-xs text-ink-muted mt-1">{provider.hint}</div>
        )}
        {conn?.error?.message && (
          <div className="text-[11px] text-amber-400 mt-1.5 flex items-start gap-1.5">
            <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>{conn.error.message}</span>
          </div>
        )}
      </div>
      <div className="flex gap-2 flex-wrap">
        <button onClick={onConnect}
          disabled={!oauthEnabled}
          title={oauthEnabled ? "Connect via OAuth (verified)" : "OAuth not configured on this deployment yet"}
          className={`text-xs inline-flex items-center gap-1.5 px-3 py-2 border ${
            oauthEnabled ? "border-volt text-volt hover:bg-volt/10" : "border-line text-ink-dim cursor-not-allowed"
          }`}
          data-testid={`connect-oauth-${provider.id}`}>
          <ShieldCheck className="w-3.5 h-3.5" /> {isOauth ? "Reconnect" : "Connect"}
        </button>
        <button onClick={onManual}
          className="text-xs inline-flex items-center gap-1.5 px-3 py-2 border border-line text-ink-muted hover:text-white"
          data-testid={`add-manual-${provider.id}`}>
          <Edit3 className="w-3.5 h-3.5" /> {isManual ? "Edit" : "Add manually"}
        </button>
        {conn && (
          <button onClick={onDisconnect} title="Disconnect"
            className="text-xs px-2 py-2 border border-line text-ink-muted hover:text-[#FF3B30]"
            data-testid={`disconnect-${provider.id}`}>
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function ManualEntryModal({ provider, existing, onClose, onSaved }) {
  const m = existing?.metrics || {};
  const [form, setForm] = useState({
    handle: existing?.handle || "",
    account_type: m.account_type || "personal",
    followers: m.followers ?? "",
    engagement_rate_pct: m.engagement_rate_pct ?? "",
    avg_reach: m.avg_reach ?? "",
    age_breakdown: m.age_breakdown?.length ? m.age_breakdown : [
      { label: "18-24", pct: 30 }, { label: "25-34", pct: 45 }, { label: "35-44", pct: 25 },
    ],
    top_countries: m.top_countries?.length ? m.top_countries : [
      { code: "United Kingdom", pct: 60 }, { code: "United States", pct: 25 },
    ],
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const updField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updAge = (i, k, v) => setForm(f => {
    const arr = [...f.age_breakdown]; arr[i] = { ...arr[i], [k]: v }; return { ...f, age_breakdown: arr };
  });
  const updCountry = (i, k, v) => setForm(f => {
    const arr = [...f.top_countries]; arr[i] = { ...arr[i], [k]: v }; return { ...f, top_countries: arr };
  });

  const save = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    setErr(""); setBusy(true);
    try {
      await api.post("/social-connections/manual", {
        provider,
        handle: form.handle,
        account_type: form.account_type,
        followers: parseInt(form.followers || 0, 10),
        engagement_rate_pct: form.engagement_rate_pct === "" ? null : parseFloat(form.engagement_rate_pct),
        avg_reach: form.avg_reach === "" ? null : parseInt(form.avg_reach, 10),
        age_breakdown: form.age_breakdown
          .filter(b => b.label && b.pct >= 0)
          .map(b => ({ label: b.label, pct: parseInt(b.pct, 10) })),
        gender_breakdown: [],
        top_countries: form.top_countries
          .filter(c => c.code && c.pct >= 0)
          .map(c => ({ code: c.code, pct: parseInt(c.pct, 10) })),
      });
      toast.success("Saved — your media kit now reflects these numbers (self-reported).");
      onSaved();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
         data-testid="manual-modal" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg bg-bg-surface border border-line max-h-[92vh] overflow-y-auto p-7">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-ink-muted hover:text-white"
                data-testid="manual-modal-close"><X className="w-5 h-5" /></button>
        <div className="label-eyebrow">Manual entry · self-reported</div>
        <h2 className="font-heading text-2xl uppercase mt-2 capitalize">{provider} audience</h2>
        <p className="text-xs text-ink-muted mt-2">These numbers will show on your media kit clearly labelled as self-reported. Switch to OAuth anytime to upgrade to verified.</p>

        <div className="grid grid-cols-2 gap-3 mt-5">
          <Field label="Handle" required>
            <input value={form.handle} onChange={(e) => updField("handle", e.target.value)}
              required placeholder="@yourname" data-testid="manual-handle"
              className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2 text-white text-sm" />
          </Field>
          <Field label="Account type">
            <select value={form.account_type}
              onChange={(e) => updField("account_type", e.target.value)}
              data-testid="manual-account-type"
              className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2 text-white text-sm">
              <option value="personal">Personal</option>
              <option value="creator">Creator</option>
              <option value="business">Business</option>
            </select>
          </Field>
          <Field label="Followers" required>
            <input value={form.followers} onChange={(e) => updField("followers", e.target.value)}
              type="number" min="0" required data-testid="manual-followers"
              className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2 text-white text-sm" />
          </Field>
          <Field label="Engagement rate %">
            <input value={form.engagement_rate_pct} onChange={(e) => updField("engagement_rate_pct", e.target.value)}
              type="number" step="0.1" min="0" max="100" placeholder="e.g. 4.2" data-testid="manual-engagement"
              className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2 text-white text-sm" />
          </Field>
          <Field label="Avg reach / post">
            <input value={form.avg_reach} onChange={(e) => updField("avg_reach", e.target.value)}
              type="number" min="0" placeholder="optional" data-testid="manual-avg-reach"
              className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2 text-white text-sm" />
          </Field>
        </div>

        <div className="mt-5">
          <div className="label-eyebrow mb-2">Age breakdown · should sum to ~100</div>
          <div className="space-y-2">
            {form.age_breakdown.map((b, i) => (
              <div key={i} className="flex gap-2" data-testid={`manual-age-row-${i}`}>
                <input value={b.label} onChange={(e) => updAge(i, "label", e.target.value)}
                  placeholder="18-24"
                  className="flex-1 bg-bg border border-line focus:border-volt outline-none px-3 py-2 text-white text-sm" />
                <input value={b.pct} onChange={(e) => updAge(i, "pct", e.target.value)}
                  type="number" min="0" max="100" placeholder="%"
                  className="w-20 bg-bg border border-line focus:border-volt outline-none px-3 py-2 text-white text-sm" />
              </div>
            ))}
          </div>
        </div>

        <div className="mt-5">
          <div className="label-eyebrow mb-2">Top countries</div>
          <div className="space-y-2">
            {form.top_countries.map((c, i) => (
              <div key={i} className="flex gap-2" data-testid={`manual-country-row-${i}`}>
                <input value={c.code} onChange={(e) => updCountry(i, "code", e.target.value)}
                  placeholder="United Kingdom"
                  className="flex-1 bg-bg border border-line focus:border-volt outline-none px-3 py-2 text-white text-sm" />
                <input value={c.pct} onChange={(e) => updCountry(i, "pct", e.target.value)}
                  type="number" min="0" max="100" placeholder="%"
                  className="w-20 bg-bg border border-line focus:border-volt outline-none px-3 py-2 text-white text-sm" />
              </div>
            ))}
          </div>
        </div>

        {err && <div className="mt-4 text-xs text-[#FF3B30]" data-testid="manual-error">{err}</div>}

        <div className="flex gap-2 mt-7">
          <button type="submit" disabled={busy}
            className="btn-volt inline-flex items-center gap-2 disabled:opacity-40" data-testid="manual-save-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {busy ? "Saving…" : "Save self-reported metrics"}
          </button>
          <button type="button" onClick={onClose} className="btn-ghost-line text-xs">Cancel</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="label-eyebrow block mb-1.5">{label}{required && <span className="text-volt ml-1">*</span>}</span>
      {children}
    </label>
  );
}
